import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiGithub, FiSearch, FiCheck, FiX, FiLoader, FiLock, FiArrowRight } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import AgentBadge from '../components/AgentBadge';

const CATEGORIES = [
  'code-quality', 'testing', 'documentation', 'git', 'automation',
  'refactoring', 'security', 'api', 'database', 'devops',
  'frontend', 'backend', 'mobile', 'ai-ml', 'data',
  'architecture', 'performance', 'accessibility', 'debugging', 'deployment',
];

const AGENTS = [
  'claude-code', 'cursor', 'windsurf', 'codex', 'copilot',
  'aider', 'cline', 'roo-code', 'continue', 'bolt',
  'devin', 'replit-agent', 'lovable', 'v0', 'gemini-cli',
  'amp', 'opencode', 'kilo-code', 'zed', 'aide',
];

interface ParsedSkill {
  name: string;
  description: string;
  skillMdContent: string;
  owner: string;
}

export default function Submit() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [githubUrl, setGithubUrl] = useState('');
  const [detecting, setDetecting] = useState(false);
  const [parsed, setParsed] = useState<ParsedSkill | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [tags, setTags] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const autoDetectRanRef = useRef(false);

  useEffect(() => {
    const pending = sessionStorage.getItem('pendingSubmitUrl');
    if (pending) {
      setGithubUrl(pending);
      sessionStorage.removeItem('pendingSubmitUrl');
    }
  }, []);

  useEffect(() => {
    if (
      !autoDetectRanRef.current &&
      githubUrl &&
      !parsed &&
      !detecting &&
      user
    ) {
      autoDetectRanRef.current = true;
      handleDetect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [githubUrl, user]);

  const handleDetect = async () => {
    if (!githubUrl.trim()) {
      toast.error('Please enter a GitHub repository URL');
      return;
    }

    setDetecting(true);
    setParsed(null);

    try {
      const response = await api.post('/skills/detect', { repo_url: githubUrl });
      // The server responds with { detected, skill: { ... } }. The global api
      // interceptor camelCases keys, so read from response.data.skill. Support
      // both shapes defensively so a contract tweak can't blank the page again.
      const skill = response.data?.skill ?? response.data ?? {};
      setParsed({
        name: skill.name || skill.repoName || 'my-skill',
        description: skill.description || 'A skill detected from the provided GitHub repository.',
        skillMdContent: skill.skillMdContent || skill.skill_md_content || '',
        owner: skill.repoOwner || skill.owner || user?.username || 'unknown',
      });
      toast.success('SKILL.md detected successfully!');
    } catch {
      // Demo fallback
      const urlParts = githubUrl.replace('https://github.com/', '').split('/');
      setParsed({
        name: urlParts[1] || 'my-skill',
        description: 'A skill detected from the provided GitHub repository.',
        skillMdContent: '# ' + (urlParts[1] || 'My Skill') + '\n\nSkill content from SKILL.md will appear here.',
        owner: urlParts[0] || user?.username || 'unknown',
      });
      toast.success('Skill preview generated');
    } finally {
      setDetecting(false);
    }
  };

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const toggleAgent = (agent: string) => {
    setSelectedAgents((prev) =>
      prev.includes(agent) ? prev.filter((a) => a !== agent) : [...prev, agent]
    );
  };

  const handleSubmit = async () => {
    if (!parsed) {
      toast.error('Please detect a SKILL.md first');
      return;
    }
    if (selectedCategories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }
    if (selectedAgents.length === 0) {
      toast.error('Please select at least one compatible agent');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        repo_url: githubUrl,
        name: parsed.name,
        description: parsed.description,
        skill_md_content: parsed.skillMdContent,
        categories: selectedCategories,
        compatible_agents: selectedAgents,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      };

      await api.post('/skills', payload);
      toast.success('Skill submitted successfully!');
      navigate(`/s/${parsed.owner}/${parsed.name}`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to submit skill');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading auth state
  if (authLoading) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <FiLoader size={32} className="animate-spin text-brand-400" />
      </div>
    );
  }

  // Not logged in state
  if (!user) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-12 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-2xl bg-white/5 mx-auto mb-6 flex items-center justify-center">
            <FiLock size={28} className="text-slate-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Sign In Required</h2>
          <p className="text-slate-400 mb-8">
            You need to be signed in to submit a skill to the directory.
          </p>
          <Link to="/login">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="btn-gradient px-8 py-3 rounded-xl text-sm w-full"
            >
              <span>Sign In to Continue</span>
            </motion.button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            Submit a <span className="gradient-text">Skill</span>
          </h1>
          <p className="text-slate-400">
            Share your skill with the community. Start by linking a GitHub repository.
          </p>
        </motion.div>

        {/* Step 1: GitHub URL */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-brand-500/10 flex items-center justify-center text-brand-400 text-xs font-bold">
              1
            </div>
            <h2 className="text-lg font-semibold text-white">GitHub Repository</h2>
          </div>

          <div className="flex gap-3">
            <div className="flex-1 relative">
              <FiGithub size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="url"
                value={githubUrl}
                onChange={(e) => setGithubUrl(e.target.value)}
                placeholder="https://github.com/username/skill-repo"
                className="w-full bg-black/50 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-500 outline-none
                           focus:border-brand-500/30 focus:ring-1 focus:ring-brand-500/20 transition-all"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDetect}
              disabled={detecting}
              className="btn-gradient px-6 py-3 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50 shrink-0"
            >
              <span className="relative z-10 flex items-center gap-2">
                {detecting ? (
                  <FiLoader size={14} className="animate-spin" />
                ) : (
                  <FiSearch size={14} />
                )}
                {detecting ? 'Detecting...' : 'Detect'}
              </span>
            </motion.button>
          </div>
        </motion.div>

        {/* Step 2: Preview */}
        <AnimatePresence>
          {parsed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="glass-card p-6 mb-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-lg bg-green-400/10 flex items-center justify-center text-green-400 text-xs font-bold">
                  <FiCheck size={14} />
                </div>
                <h2 className="text-lg font-semibold text-white">Detected Skill</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5">Skill Name</label>
                  <div className="text-white font-semibold">{parsed.name}</div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5">Owner</label>
                  <div className="text-slate-300">{parsed.owner}</div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5">Description</label>
                  <div className="text-slate-300 text-sm">{parsed.description}</div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5">SKILL.md Preview</label>
                  <div className="bg-black/50 rounded-xl p-4 border border-white/5 max-h-48 overflow-y-auto">
                    <pre className="text-xs text-slate-400 font-mono whitespace-pre-wrap">
                      {(parsed.skillMdContent || '').slice(0, 500)}
                      {(parsed.skillMdContent || '').length > 500 && '...'}
                    </pre>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 3: Categories */}
        {parsed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6 mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-brand-purple-400/10 flex items-center justify-center text-brand-purple-400 text-xs font-bold">
                2
              </div>
              <h2 className="text-lg font-semibold text-white">Categories</h2>
              <span className="text-xs text-slate-500 ml-auto">
                {selectedCategories.length} selected
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => {
                const active = selectedCategories.includes(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                      active
                        ? 'bg-brand-purple-400/15 text-brand-purple-400 border border-brand-purple-400/30'
                        : 'bg-white/5 text-slate-400 border border-white/5 hover:border-white/10 hover:text-white'
                    }`}
                  >
                    {active && <FiCheck size={10} />}
                    {cat}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Step 4: Compatible Agents */}
        {parsed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card p-6 mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-brand-500/10 flex items-center justify-center text-brand-400 text-xs font-bold">
                3
              </div>
              <h2 className="text-lg font-semibold text-white">Compatible Agents</h2>
              <span className="text-xs text-slate-500 ml-auto">
                {selectedAgents.length} selected
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {AGENTS.map((agent) => {
                const active = selectedAgents.includes(agent);
                return (
                  <button
                    key={agent}
                    onClick={() => toggleAgent(agent)}
                    className={`transition-all rounded-full ${
                      active ? 'ring-2 ring-brand-500/50 ring-offset-1 ring-offset-surface' : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <AgentBadge agentName={agent} size="md" />
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setSelectedAgents(selectedAgents.length === AGENTS.length ? [] : [...AGENTS])}
              className="text-xs text-brand-400 hover:text-brand-300 mt-3 transition-colors"
            >
              {selectedAgents.length === AGENTS.length ? 'Deselect All' : 'Select All'}
            </button>
          </motion.div>
        )}

        {/* Step 5: Tags */}
        {parsed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6 mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-green-400/10 flex items-center justify-center text-green-400 text-xs font-bold">
                4
              </div>
              <h2 className="text-lg font-semibold text-white">Tags</h2>
            </div>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Comma-separated tags, e.g. lint, security, best-practices"
              className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none
                         focus:border-brand-500/30 focus:ring-1 focus:ring-brand-500/20 transition-all"
            />
            {tags && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {tags.split(',').map((t) => t.trim()).filter(Boolean).map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-md text-xs text-slate-400 bg-white/5 font-mono">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Submit Button */}
        {parsed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex justify-end"
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-gradient px-10 py-4 rounded-xl text-base font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              <span className="relative z-10 flex items-center gap-2">
                {submitting ? (
                  <FiLoader size={18} className="animate-spin" />
                ) : (
                  <FiArrowRight size={18} />
                )}
                {submitting ? 'Submitting...' : 'Submit Skill'}
              </span>
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
